import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useRef } from 'react';
import { usePageView } from '@koob/google-tag-manager';
import { AuthContext } from './contexts';
import AppProvider from 'providers/AppProvider';
import { lazy } from 'react';
import { Navigate } from 'react-router';
import Folders from './containers/Folders';
import BookingProvider from './providers/BookingProvider';
import ExperienceBookingProvider from './providers/ExperienceBookingProvider';
import LanguageProvider from 'providers/LanguageProvider';
import { NotificationsProvider } from '@koob/notifications';
import * as notificationsApi from 'api/notifications';
import TripDesignerConfigProvider from './providers/TripDesignerConfigProvider';
import TripDesignerRoutes from './containers/TripDesigner';
// KD-3735: one-organization pages ask which organization in the group view.
import RequireActingOrganization from 'components/RequireActingOrganization';
import NotFound from './containers/NotFound';
import TripsRoutes from './containers/Trips';
import SettingsModal from 'components/SettingsModal';
import { isSettingsPath } from 'components/settingsSections';
import ProfileContainer from './containers/Profile';
import { KoobMapsProvider } from '@koob/maps';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from './constants';

const Organizations = lazy(() => import('containers/Organizations'));
const Users = lazy(() => import('containers/Users'));
const Hotels = lazy(() => import('containers/Hotels'));
const Bookings = lazy(() => import('containers/Bookings'));
const Dmcs = lazy(() => import('containers/Dmcs'));
const Experiences = lazy(() => import('containers/Experiences'));
const McpAuthorize = lazy(() => import('containers/McpAuthorize'));

const MapsWrapper = ({ children }) => {
  return (
    <KoobMapsProvider apiKey={GOOGLE_MAPS_API_KEY} mapId={GOOGLE_MAPS_MAP_ID}>
      {children}
    </KoobMapsProvider>
  );
};

const App = () => {
  const { accessToken, logout, isActiveToken } = useContext(AuthContext);
  const location = useLocation();
  usePageView(location);
  const navigate = useNavigate();

  // Settings pages open in a modal over the last other page, which stays
  // rendered beneath. A settings URL opened directly has none: nothing shows
  // beneath (a page mounting there may rewrite the URL to its own and close
  // the modal), and closing goes to the folders.
  const lastPage = useRef(null);
  const inSettings = isSettingsPath(location.pathname);
  if (!inSettings) lastPage.current = location;
  const page = lastPage.current;

  if (!Boolean(accessToken)) {
    // Carry the target so Login can come back (deep links, MCP consent).
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (!isActiveToken) {
    logout();
  }

  return (
    <MapsWrapper>
      <AppProvider>
        <BookingProvider>
          <ExperienceBookingProvider>
            <LanguageProvider>
              <TripDesignerConfigProvider>
                <NotificationsProvider api={notificationsApi}>
                  {page && (
                    <Routes location={page}>
                      <Route
                        path="/"
                        element={<Navigate to="/folders" replace />}
                      />
                      <Route
                        path="hotels/*"
                        element={
                          <RequireActingOrganization>
                            <Hotels />
                          </RequireActingOrganization>
                        }
                      />
                      <Route
                        path="experiences/*"
                        element={
                          <RequireActingOrganization>
                            <Experiences />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="bookings/*" element={<Bookings />} />
                      <Route path="users/*" element={<Users />} />
                      <Route
                        path="organizations/*"
                        element={<Organizations />}
                      />
                      <Route
                        path="dmcs/*"
                        element={
                          <RequireActingOrganization>
                            <Dmcs />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="folders/*" element={<Folders />} />
                      <Route
                        path="trip-designer/*"
                        element={
                          <RequireActingOrganization>
                            <TripDesignerRoutes />
                          </RequireActingOrganization>
                        }
                      />
                      <Route path="trips/*" element={<TripsRoutes />} />
                      <Route path="user/*" element={<ProfileContainer />} />
                      <Route path="mcp/authorize" element={<McpAuthorize />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  )}
                  <SettingsModal
                    open={inSettings}
                    onClose={() =>
                      navigate(page ?? '/folders', { state: page?.state })
                    }
                  />
                </NotificationsProvider>
              </TripDesignerConfigProvider>
            </LanguageProvider>
          </ExperienceBookingProvider>
        </BookingProvider>
      </AppProvider>
    </MapsWrapper>
  );
};

export default App;
